import React, { useState } from "react";
import Link from "next/link";
import { Collapse, List, ListItem } from "@mui/material";
import { ChevrondownSvg } from "@/src/Asset";

interface Item {
  id: number;
  country_name?: string;
  state_name?: string;
  city_name?: string;
  country_url?: string;
  state_url?: string;
  city_url?: string;
}

const RedirectSectionItem = ({ item, type }: { item: Item; type: string }) => {
  const getRedirectUrl = () => {
    switch (type) {
      case "Study Abroad":
        return item?.country_url
          ? item.country_url.replace("study-in-", "colleges-in-")
          : "#";
      case "States":
        return item?.state_url ? `colleges-in-${item.state_url}` : "#";
      case "Top Cities":
        return item?.city_url ? `colleges-in-${item.city_url}` : "#";
      default:
        return "#";
    }
  };

  let displayName = "";
  if (type === "Study Abroad") displayName = item.country_name ?? "";
  else if (type === "States") displayName = item.state_name ?? "";
  else if (type === "Top Cities") displayName = item.city_name ?? "";

  if (!displayName) return null;

  return (
    <Link href={`/${getRedirectUrl()}`}>
      <span className="line-clamp-2 text-sm text-white font-light">
        {displayName}
      </span>
    </Link>
  );
};

const ListData = ({
  topClg,
  streams,
  cities,
  states,
  countries,
}: Record<string, any[]>) => {
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    topClg: false,
    streams: false,
    cities: false,
    states: false,
    countries: false,
  });

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 xl:gap-x-6 gap-x-2 md:mt-8 md:border-none">
      {/* Desktop View */}
      {topClg?.length > 0 && (
        <div className="md:flex hidden flex-col gap-2.5 font-light">
          <div className="font-semibold text-base">Top Colleges</div>
          {topClg.map((topClgData) => {
            const isOnlineCollege = topClgData?.is_online_college;
            const collegeUrl = isOnlineCollege
              ? `/colleges/${topClgData?.college_url}`
              : `/colleges/${topClgData?.college_url}/college-info`;
            return (
              <Link key={topClgData.id} href={collegeUrl}>
                <span className="line-clamp-2 text-sm text-white font-light">
                  {topClgData?.abbreviation}
                </span>
              </Link>
            );
          })}
        </div>
      )}

      {streams?.length > 0 && (
        <div className="md:flex hidden flex-col gap-2.5 font-light">
          <div className="font-semibold text-base">Top Universities</div>
          {streams.map((streamsData) => (
            <Link
              key={streamsData.id}
              href={`/${streamsData?.stream_name
                ?.toLowerCase()
                ?.replace(/\s+/g, "-")}-colleges-in-india`}
            >
              <span className="line-clamp-2 text-sm text-white font-light">
                {streamsData?.stream_name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {cities?.length > 0 && (
        <div className="md:flex hidden flex-col gap-2.5 font-light">
          <div className="font-semibold text-base">Top Cities</div>
          {cities.map((cityData) => (
            <RedirectSectionItem
              key={cityData?.id}
              item={cityData}
              type="Top Cities"
            />
          ))}
        </div>
      )}

      {states?.length > 0 && (
        <div className="md:flex hidden flex-col gap-2.5 font-light">
          <div className="font-semibold text-base">States</div>
          {states.map((stateData) => (
            <RedirectSectionItem
              key={stateData?.id}
              item={stateData}
              type="States"
            />
          ))}
        </div>
      )}

      {countries?.length > 0 && (
        <div className="md:flex hidden flex-col gap-2.5 font-light">
          <div className="font-semibold text-base">Study Abroad</div>
          {countries.map((countriesData) => (
            <RedirectSectionItem
              key={countriesData?.id}
              item={countriesData}
              type="Study Abroad"
            />
          ))}
        </div>
      )}

      {/* Mobile View with Collapsible Sections */}
      <div className={`grid sm:grid-cols-3 grid-cols-1 md:hidden`}>
        {/* Top Colleges */}
        {topClg?.length > 0 && (
          <div className="flex flex-col gap-1 md:gap-4 text-xs font-light">
            <List>
              <button
                onClick={() => toggleSection("topClg")}
                className="flex justify-between px-2 cursor-pointer items-center gap-x-2"
              >
                <p className="text-white text-base">Top Colleges</p>
                <ChevrondownSvg
                  className={`scale-75 ${
                    openSections.topClg ? "rotate-180" : ""
                  }`}
                />
              </button>
              <Collapse in={openSections.topClg} timeout="auto" unmountOnExit>
                <List component="div">
                  {topClg.map((topClgData) => {
                    const isOnlineCollege = topClgData?.is_online_college;
                    const collegeUrl = isOnlineCollege
                      ? `/colleges/${topClgData?.college_url}`
                      : `/colleges/${topClgData?.college_url}/college-info`;
                    return (
                      <ListItem key={topClgData.id}>
                        <Link href={collegeUrl}>
                          <span className="line-clamp-2 text-sm">
                            {topClgData?.abbreviation}
                          </span>
                        </Link>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </List>
          </div>
        )}

        {/* Top Universities */}
        {streams?.length > 0 && (
          <div className="flex flex-col gap-1 md:gap-4 text-xs font-light">
            <List>
              <button
                onClick={() => toggleSection("streams")}
                className="flex justify-between px-2 cursor-pointer items-center gap-x-2"
              >
                <p className="text-white text-base">Top Universities</p>
                <ChevrondownSvg
                  className={`scale-75 ${
                    openSections.streams ? "rotate-180" : ""
                  }`}
                />
              </button>
              <Collapse in={openSections.streams} timeout="auto" unmountOnExit>
                <List component="div">
                  {streams.map((streamsData) => (
                    <ListItem key={streamsData.id}>
                      <Link
                        href={`/${streamsData?.stream_name
                          ?.toLowerCase()
                          ?.replace(/\s+/g, "-")}-colleges-in-india`}
                      >
                        <span className="line-clamp-2 text-sm">
                          {streamsData?.stream_name}
                        </span>
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
          </div>
        )}

        {/* Top Cities */}
        {cities?.length > 0 && (
          <div className="flex flex-col gap-1 md:gap-4 text-xs font-light">
            <List>
              <button
                onClick={() => toggleSection("cities")}
                className="flex justify-between px-2 cursor-pointer items-center gap-x-2"
              >
                <p className="text-white text-base">Top Cities</p>
                <ChevrondownSvg
                  className={`scale-75 ${
                    openSections.cities ? "rotate-180" : ""
                  }`}
                />
              </button>
              <Collapse in={openSections.cities} timeout="auto" unmountOnExit>
                <List component="div">
                  {cities.map((cityData) => (
                    <ListItem key={cityData.id}>
                      <RedirectSectionItem
                        key={cityData?.id}
                        item={cityData}
                        type="Top Cities"
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
          </div>
        )}

        {/* States */}
        {states?.length > 0 && (
          <div className="flex flex-col gap-1 md:gap-4 text-xs font-light">
            <List>
              <button
                onClick={() => toggleSection("states")}
                className="flex justify-between px-2 cursor-pointer items-center gap-x-2"
              >
                <p className="text-white text-base">States</p>
                <ChevrondownSvg
                  className={`scale-75 ${
                    openSections.states ? "rotate-180" : ""
                  }`}
                />
              </button>
              <Collapse in={openSections.states} timeout="auto" unmountOnExit>
                <List component="div">
                  {states.map((stateData) => (
                    <ListItem key={stateData.id}>
                      <RedirectSectionItem
                        key={stateData?.id}
                        item={stateData}
                        type="States"
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
          </div>
        )}

        {/* Study Abroad */}
        {countries?.length > 0 && (
          <div className="flex flex-col gap-1 md:gap-4 text-xs font-light">
            <List>
              <button
                onClick={() => toggleSection("countries")}
                className="flex justify-between px-2 cursor-pointer items-center gap-x-2"
              >
                <p className="text-white text-base">Study Abroad</p>
                <ChevrondownSvg
                  className={`scale-75 ${
                    openSections.countries ? "rotate-180" : ""
                  }`}
                />
              </button>
              <Collapse
                in={openSections.countries}
                timeout="auto"
                unmountOnExit
              >
                <List component="div">
                  {countries.map((countriesData) => (
                    <ListItem key={countriesData.id}>
                      <RedirectSectionItem
                        item={countriesData}
                        type="Study Abroad"
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListData;
